// Adapted from our own Immersive Worlds CameraAuthority.
// Source: Juanmaes83/escaparates-pro
// labs/immersive-worlds/engine/camera/camera-authority.js
// blob 7527a7001a6c0b9b112c49184c03ef9085692589
//
// Invariant: exactly one authoritative camera controller may write per frame.
// Plain-number poses keep this layer renderer-independent.

import { CAMERA_AUTHORITY } from './types.js';
import { EVENTS } from './event-bus.js';

export class CameraAuthority {
  constructor({ bus, pose, reducedMotion = false } = {}) {
    this.bus = bus;
    this.reducedMotion = reducedMotion;
    this.pose = pose ? clonePose(pose) : { position: [0, 1.62, 4], target: [0, 1.5, 0], fov: 50 };
    this._controllers = new Map();
    this._owner = null;
    this._frame = 0;
    this._token = null;
    this._tokenUsed = false;
    this._transition = null;
    this.violations = [];
    this.history = [];
  }

  register(name, controller) {
    if (!Object.values(CAMERA_AUTHORITY).includes(name) || name === CAMERA_AUTHORITY.TRANSITION) {
      throw new Error(`[WCB] unknown/register-forbidden camera authority: ${name}`);
    }
    if (typeof controller?.update !== 'function') {
      throw new Error(`[WCB] camera controller ${name} requires update()`);
    }
    this._controllers.set(name, controller);
    if (!this._owner) this._owner = name;
    return this;
  }

  get owner() {
    return this._transition ? CAMERA_AUTHORITY.TRANSITION : this._owner;
  }

  get isTransitioning() {
    return this._transition !== null;
  }

  request(name, options = {}) {
    if (!this._controllers.has(name)) throw new Error(`[WCB] no registered camera controller: ${name}`);
    if (name === this._owner && !this._transition) return false;

    const from = this._owner;
    const reason = options.reason ?? 'unspecified';
    const restore = options.restore ?? 'ADOPT_INCOMING';
    const endPose = options.endPose ? clonePose(options.endPose) : null;
    const durationMs = this.reducedMotion ? 0 : (options.durationMs ?? 0);

    this._controllers.get(from)?.onLose?.({ to: name });
    this.history.push({ from, to: name, reason, durationMs, frame: this._frame });
    this.bus?.emit(EVENTS.CAMERA_AUTHORITY_CHANGED, { from, to: name, reason });

    if (durationMs > 0 && endPose) {
      this._transition = {
        from,
        to: name,
        elapsed: 0,
        duration: durationMs / 1000,
        startPose: clonePose(this.pose),
        endPose,
        restore,
        reason
      };
      this._owner = name;
      return true;
    }

    this._owner = name;
    if (endPose) this.pose = clonePose(endPose);
    this._controllers.get(name)?.onGain?.(clonePose(this.pose), { reason, restore });
    return true;
  }

  update(dt) {
    this._frame += 1;
    this._tokenUsed = false;

    if (this._transition) {
      const t = this._transition;
      t.elapsed += dt;
      const k = t.duration <= 0 ? 1 : Math.min(t.elapsed / t.duration, 1);
      const e = easeInOutCubic(k);
      this.pose = {
        position: lerpVec3(t.startPose.position, t.endPose.position, e),
        target: lerpVec3(t.startPose.target, t.endPose.target, e),
        fov: t.startPose.fov + (t.endPose.fov - t.startPose.fov) * e
      };
      if (k >= 1) {
        const { to, reason, restore } = t;
        this._transition = null;
        this._controllers.get(to)?.onGain?.(clonePose(this.pose), { reason, restore });
      }
      return clonePose(this.pose);
    }

    const controller = this._controllers.get(this._owner);
    if (!controller) return clonePose(this.pose);

    const token = { frame: this._frame, owner: this._owner };
    this._token = token;
    const commit = (pose) => this._commit(token, pose);
    controller.update(dt, commit, clonePose(this.pose));
    this._token = null;
    return clonePose(this.pose);
  }

  _commit(token, pose) {
    if (!token || token !== this._token) return this._violation('stale or foreign write token', token);
    if (token.frame !== this._frame) return this._violation(`token from frame ${token.frame} used on frame ${this._frame}`, token);
    if (token.owner !== this._owner) return this._violation(`${token.owner} wrote while ${this._owner} was authoritative`, token);
    if (this._tokenUsed) return this._violation(`two camera writes in frame ${this._frame}`, token);

    assertPose(pose);
    this._tokenUsed = true;
    this.pose = {
      position: [...pose.position],
      target: [...pose.target],
      fov: pose.fov ?? this.pose.fov
    };
    return clonePose(this.pose);
  }

  _violation(message, token) {
    const record = { message, frame: this._frame, owner: this._owner, token: token?.owner ?? null };
    this.violations.push(record);
    throw new Error(`[WCB] camera authority violation — ${message}`);
  }

  report() {
    return {
      owner: this.owner,
      frame: this._frame,
      transitioning: this.isTransitioning,
      violations: this.violations.length,
      violationDetail: this.violations.slice(-5),
      history: this.history.slice(-12),
      pose: clonePose(this.pose)
    };
  }
}

export function clonePose(pose) {
  assertPose(pose);
  return { position: [...pose.position], target: [...pose.target], fov: pose.fov };
}

function assertPose(pose) {
  if (!pose || !Array.isArray(pose.position) || pose.position.length !== 3 || !Array.isArray(pose.target) || pose.target.length !== 3) {
    throw new Error('[WCB] camera pose requires position[3] and target[3]');
  }
}

function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

function lerpVec3(a, b, t) {
  return [
    a[0] + (b[0] - a[0]) * t,
    a[1] + (b[1] - a[1]) * t,
    a[2] + (b[2] - a[2]) * t
  ];
}
